import React from 'react';
import { Banknote, Bell, Building2, CalendarCheck, CalendarOff, CalendarSync, CalendarX, CreditCard, Landmark, Tags, Users, Wallet } from 'lucide-react';
import { cn } from '../../lib/utils';
import Aparecer from './Aparecer';
import ContadorAnimado from './ContadorAnimado';
import { TONOS } from './tonos';
import mercadoPago from '../../assets/images/Mercado/mercadopago.webp';

/**
 * El mosaico de funcionalidades de la landing: el SEGUNDO nivel de la sección.
 *
 * El primero son las filas de `FilaDeFuncionalidad`, que van arriba y explican
 * dos o tres features con su propio dibujo al lado. Acá abajo entra todo lo
 * demás, enumerado. Cuando el mosaico era la sección entera, las once
 * funcionalidades pesaban casi lo mismo y ninguna estaba explicada de verdad.
 *
 * Piezas de distinto tamaño en una misma grilla: la que ocupa más lugar es la
 * que más pesa a la hora de decidir —el dato de inasistencias—, y las
 * secundarias entran en las celdas chicas. Antes eran ocho tarjetas del mismo
 * tamaño, que es una lista disfrazada: todo parece pesar lo mismo y no hay
 * dónde apoyar la vista.
 *
 * EL BOT DE WHATSAPP YA NO ESTÁ ACÁ: se fue a su propia fila, arriba, con el
 * dibujo del chat anotando el turno en la agenda (`DelChatALaAgenda`). Estaba
 * montando `SimuladorWhatsApp` por segunda vez en la misma página —el hero ya lo
 * muestra— y encima sin decir en qué plan entra, que es el error que la landing
 * viene evitando en el hero. No volver a agregarlo acá.
 *
 *
 * UN TÍTULO Y UNA LÍNEA POR PIEZA
 *
 * La bajada de cada pieza es una sola oración corta, y lo que falta lo cuenta
 * el dibujo: los medios de pago, la grilla de precios, los tres bloqueos de
 * ejemplo, la conversación del bot. Con tres o cuatro renglones por pieza el
 * mosaico se vuelve una pared de texto que nadie lee y las tarjetas quedan
 * disparejas de alto. Si algo no se entiende, es más corto agregarlo al dibujo
 * que explicarlo en la bajada.
 *
 * Las piezas están puestas desde que carga la página: la entrada escalonada se
 * sacó de toda la landing (components/business/Aparecer.jsx). Lo que sí se
 * anima es el contenido de las piezas —los números cuentan hasta su valor y el
 * bot escribe una conversación—, y eso arranca cuando la pieza está a la vista.
 * Se apaga solo si el sistema pide menos movimiento.
 *
 *
 * EL ANCHO DE CADA PIEZA NO ES DECORATIVO: TIENE QUE CERRAR LA FILA
 *
 * En lg la grilla tiene cuatro columnas y las piezas se acomodan solas en el
 * orden en que están escritas, sin rellenar huecos hacia atrás. Así quedan hoy:
 *
 *   filas 1-2  dato (2 anchos, 2 altos)  |  seña (2)
 *                                        |  precios (2)
 *   fila  3    tu equipo (2)             |  tus lugares (2)
 *   fila  4    bloqueos (4)
 *   fila  5    recordatorios (1)  calendarios (2)  agenda 24/7 (1)
 *   fila  6    el cobro es tuyo (4)
 *
 * Cada fila suma exactamente cuatro. Una pieza nueva que no complete su fila
 * deja el hueco a la derecha —ya pasó con "sin comisiones", que por eso terminó
 * a lo ancho—, así que al agregar una hay que sumar de nuevo la fila donde cae
 * y, si hace falta, cambiarle el ancho a alguna vecina.
 *
 * "Tu equipo" y "Tus lugares" pasaron de una columna a dos cuando salió el bot:
 * su hueco era de 2 anchos por 2 altos, y con esas dos piezas en una columna la
 * fila 3 sumaba 2 y dejaba media fila vacía a la derecha. De paso les vino bien
 * el lugar, que con una sola columna les quedaba justo.
 */

/**
 * La caja de cada pieza.
 *
 * Sigue envuelta en `Aparecer` aunque hoy no anime: es el único lugar donde
 * volvería a vivir la entrada si alguna vez vuelve, y `orden` ya está puesto
 * para eso. Estuvo escrita acá adentro y era la única entrada de la página, así
 * que el mosaico se animaba y todo lo demás ya estaba puesto.
 */
function Pieza({ orden = 0, className = '', children }) {
  return (
    <Aparecer
      orden={orden}
      como="article"
      // `cn` y no concatenar: con un `+` las dos clases quedan en el atributo y
      // gana la que esté más abajo en la hoja de Tailwind, no la última
      // escrita. La pieza del dato pedía `bg-primary` y le ganaba el `bg-white`
      // de acá: quedaba un rectángulo blanco con el texto blanco encima, o sea
      // invisible. `cn` usa tailwind-merge, que descarta la que pisa.
      className={cn(
        'relative flex flex-col overflow-hidden rounded-3xl border border-neutral-200 bg-white p-6',
        className
      )}
    >
      {children}
    </Aparecer>
  );
}

/* Los colores de las piezas viven en `tonos.js`: los comparte con las filas de
   `FilaDeFuncionalidad`, que son la otra mitad de esta sección. Estaban escritos
   acá adentro cuando el mosaico era lo único que había. */

/**
 * Dos filas de la grilla de precios, igual que se ve en el panel: en blanco el
 * precio base y en violeta el que se le puso a esa franja. Los montos son de
 * muestra —no salen de ningún negocio— y están elegidos para que se entienda de
 * un vistazo que el fin de semana a la tarde sale más.
 */
const DIAS_MUESTRA = ['Jue', 'Vie', 'Sáb', 'Dom'];
const PRECIOS_MUESTRA = [
  { monto: '$8.000' },
  { monto: '$8.000' },
  { monto: '$12.000', propio: true },
  { monto: '$12.000', propio: true },
  { monto: '$8.000' },
  { monto: '$10.000', propio: true },
  { monto: '$12.000', propio: true },
  { monto: '$12.000', propio: true },
];

/**
 * Las tres cosas que se pueden tapar.
 *
 * Antes eran tres casos inventados con nombre y fecha ("Lucía, de licencia 7
 * días"). Un caso hay que leerlo entero y después deducir la regla; esto dice
 * la regla directamente, que es lo único que hace falta entender: se tapa una
 * persona, un lugar, o todo junto.
 */
const BLOQUEOS_MUESTRA = [
  { icono: Users, que: 'Una persona' },
  { icono: Building2, que: 'Un lugar' },
  { icono: CalendarX, que: 'Todo el negocio' },
];

/**
 * Las caritas del equipo.
 *
 * Son emojis y no iniciales: "LM" en un círculo no es nadie hasta que sabés de
 * quién son esas letras, y ocupaba el mismo lugar diciendo menos.
 *
 * Personas a secas, sin oficio. Estaban con 💇 y 🧑‍🏫 —alguien cortándose el
 * pelo, alguien con un pizarrón atrás—, y eso convierte al equipo en el de una
 * peluquería o el de una escuela. Acá el equipo es de cualquier rubro, así que
 * la cara tiene que ser sólo una cara. Además, a 16px adentro de un círculo, el
 * pizarrón y la tijera son manchas que no se distinguen.
 */
const EQUIPO_MUESTRA = ['👩', '🧑', '👨'];

/**
 * Los calendarios donde termina el turno.
 *
 * No es una lista de deseos: sale de backend/calendarInvite.js, que adjunta un
 * .ics al mail de confirmación, y su propio comentario nombra a los que lo
 * entienden. Si alguna vez se saca ese adjunto, esta lista miente.
 *
 * Google va marcado aparte porque hace algo más: se conecta por OAuth y anda
 * en los dos sentidos —lo que bloqueás allá deja de ofrecerse acá—. Los otros
 * dos reciben el turno, pero no avisan para atrás. Prometer "sincronizado"
 * para los tres sería vender algo que no pasa.
 *
 * Thunderbird sale de la lista aunque el .ics también le entre: no lo usa nadie
 * del público al que le hablamos, y leerlo ahí no suma un calendario más, les
 * resta credibilidad a los otros. La lista está para que alguien se reconozca,
 * no para ser exhaustiva.
 */
const CALENDARIOS = [
  { nombre: 'Google Calendar', doblesentido: true },
  { nombre: 'Apple Calendar' },
  { nombre: 'Outlook' },
];

/** Título chico con ícono, el encabezado repetido de las piezas secundarias. */
function Encabezado({ icono: Icono, tono = 'naranja', children }) {
  const { chip, icono } = TONOS[tono] ?? TONOS.naranja;

  return (
    <div className="flex items-center gap-2">
      <span className={cn('flex h-8 w-8 shrink-0 items-center justify-center rounded-full', chip)}>
        <Icono className={cn('h-4 w-4', icono)} aria-hidden="true" />
      </span>
      <h3 className="text-base font-semibold text-neutral-900">{children}</h3>
    </div>
  );
}

export default function MosaicoFuncionalidades() {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
      {/* --------------------------------------------------------------
          El dato. Es la pieza más grande porque es el argumento que más
          mueve la aguja: cuánto se deja de perder por gente que no viene.
          -------------------------------------------------------------- */}
      <Pieza orden={0} className="justify-between bg-primary text-primary-foreground sm:col-span-2 lg:row-span-2">
        <div>
          <p className="text-sm font-semibold uppercase tracking-wide text-primary-foreground/80">
            Menos ausencias
          </p>
          <p className="mt-6 flex items-baseline gap-1 text-7xl font-bold leading-none tracking-tight sm:text-8xl">
            <ContadorAnimado hasta={82} sufijo="%" />
          </p>
          {/* Empieza en mayúscula y se explica solo: colgado del número como
              una frase cortada ("menos turnos perdidos con…") no se entendía
              menos respecto de qué. */}
          <p className="mt-4 max-w-sm text-base leading-relaxed text-primary-foreground/90">
            La gente falta menos cuando ya pagó y le avisaste.
          </p>
        </div>

        <ul className="mt-8 flex flex-wrap gap-2">
          {['Recordatorios', 'Seña al reservar', 'Lista de espera'].map((t) => (
            <li
              key={t}
              className="rounded-full bg-white/15 px-3 py-1.5 text-sm font-medium backdrop-blur"
            >
              {t}
            </li>
          ))}
        </ul>
      </Pieza>

      {/* Acá vivía el bot de WhatsApp, la pieza más alta del mosaico, con el
          simulador escribiendo la conversación adentro. Se mudó a su propia
          fila arriba de esta grilla (components/business/FilaDeFuncionalidad.jsx
          + DelChatALaAgenda.jsx), donde el chat va al lado de la agenda y se ve
          que el turno se anota solo. Ver el comentario de arriba del archivo. */}

      {/* Cobros. Dos cosas que importan: que se activa y se desactiva, y que
          no es solo Mercado Pago —el producto acepta también transferencia y
          efectivo, así que decir solo "Mercado Pago" dejaba afuera a quien
          cobra de otra forma. */}
      <Pieza orden={1} className="bg-violet-50 sm:col-span-2">
        <Encabezado icono={CreditCard} tono="violeta">
          Seña para reservar
        </Encabezado>
        <p className="mt-3 text-sm leading-relaxed text-neutral-600">
          Cobrás una parte antes de que venga. Vos elegís cuánto.
        </p>

        <ul className="mt-5 flex flex-wrap items-center gap-2">
          <li className="flex items-center gap-2 rounded-full bg-white px-3 py-1.5 text-sm font-medium text-neutral-700 shadow-sm">
            <img src={mercadoPago} alt="" className="h-5 w-5 object-contain" />
            Mercado Pago
          </li>
          <li className="flex items-center gap-2 rounded-full bg-white px-3 py-1.5 text-sm font-medium text-neutral-700 shadow-sm">
            <Landmark className="h-4 w-4 text-violet-600" aria-hidden="true" />
            Transferencia
          </li>
          <li className="flex items-center gap-2 rounded-full bg-white px-3 py-1.5 text-sm font-medium text-neutral-700 shadow-sm">
            <Banknote className="h-4 w-4 text-violet-600" aria-hidden="true" />
            Efectivo
          </li>
        </ul>

        <div className="mt-4 flex items-center justify-between gap-4 rounded-2xl bg-white/70 p-3">
          <span className="truncate text-sm font-medium text-neutral-700">
            Pedir seña en este servicio
          </span>
          {/* Es una ilustración de la opción, no un control: por eso no es un
              <button> ni recibe foco. */}
          <span
            className="flex h-6 w-11 shrink-0 items-center rounded-full bg-violet-600 p-0.5"
            aria-hidden="true"
          >
            <span className="ml-auto h-5 w-5 rounded-full bg-white shadow-sm" />
          </span>
        </div>
      </Pieza>

      {/* --------------------------------------------------------------
          Precios por franja. Va en violeta como el resto de lo que tiene
          que ver con plata. La grillita de abajo es la misma que se usa
          para configurarlo (components/PricingCalendar.jsx): mostrarla
          ahorra explicar cómo se cargan.
          -------------------------------------------------------------- */}
      <Pieza orden={2} className="bg-rose-50/60 sm:col-span-2">
        <Encabezado icono={Tags} tono="rosa">
          Precios por día y por hora
        </Encabezado>
        <p className="mt-3 text-sm leading-relaxed text-neutral-600">
          Podés cobrar más caro en las horas que más te piden.
        </p>

        <div className="mt-5 grid grid-cols-4 gap-1.5" aria-hidden="true">
          {DIAS_MUESTRA.map((dia) => (
            <span
              key={dia}
              className="text-center text-[11px] font-semibold uppercase tracking-wide text-neutral-400"
            >
              {dia}
            </span>
          ))}
          {PRECIOS_MUESTRA.map(({ monto, propio }, i) => (
            <span
              key={i}
              className={cn(
                'rounded-lg py-2 text-center text-[11px] font-semibold',
                propio ? 'bg-rose-600 text-white' : 'bg-white text-neutral-500 shadow-sm'
              )}
            >
              {monto}
            </span>
          ))}
        </div>
      </Pieza>

      {/* Personal. Las caras son emojis y no iniciales: "LM" no es nadie hasta
          que sabés de quién son, y ocupaba el mismo lugar diciendo menos.
          Dos columnas —antes una— para cerrar la fila que dejó abierta la
          salida del bot; ver el mapa de filas arriba del archivo. */}
      <Pieza orden={3} className="justify-between bg-teal-50/70 lg:col-span-2">
        <div>
          <Encabezado icono={Users} tono="agua">
            Tu equipo
          </Encabezado>
          <p className="mt-3 text-sm leading-relaxed text-neutral-600">
            Cada uno tiene su horario y lo que sabe hacer.
          </p>
        </div>

        <div className="mt-6 flex items-center gap-3">
          <ul className="flex -space-x-2" aria-hidden="true">
            {EQUIPO_MUESTRA.map((emoji) => (
              <li
                key={emoji}
                className="flex h-9 w-9 items-center justify-center rounded-full border-2 border-white bg-teal-100"
              >
                {/* `emoji-flag` va en un span adentro y NO en el <li>.
                    Esa clase declara `display: inline-block` (Home.css), que
                    pisa al `flex` del contenedor porque las dos son una clase
                    sola y gana la hoja que carga última. Sin flex no hay
                    `items-center`, y el emoji se apoyaba en la base del
                    renglón en vez de quedar en el centro del círculo. */}
                <span className="emoji-flag text-base leading-none">{emoji}</span>
              </li>
            ))}
          </ul>
          <span className="text-sm font-medium text-neutral-700">El cliente elige con quién</span>
        </div>
      </Pieza>

      {/* Lugares. Los chips son los tipos de lugar que la gente reserva; se
          sacó "Sillones", que no se entiende sin explicarlo, y "Canchas", que
          nombra un rubro al que hoy no le estamos hablando —el visitante que
          buscamos es un consultorio o un centro con equipo, y un chip que
          arranca con canchas lo manda a pensar que la página no es para él. */}
      <Pieza orden={4} className="justify-between bg-amber-50/60 lg:col-span-2">
        <div>
          <Encabezado icono={Building2} tono="ambar">
            Tus lugares
          </Encabezado>
          <p className="mt-3 text-sm leading-relaxed text-neutral-600">
            Cada lugar tiene su propia agenda, así entran varios turnos a la misma hora.
          </p>
        </div>

        <ul className="mt-6 flex flex-wrap gap-1.5">
          {['Consultorios', 'Salas', 'Gabinetes', 'Sucursales'].map((t) => (
            <li
              key={t}
              className="rounded-full bg-white px-2.5 py-1 text-xs font-medium text-neutral-700 shadow-sm"
            >
              {t}
            </li>
          ))}
        </ul>
      </Pieza>

      {/* --------------------------------------------------------------
          Bloqueos. Va a lo ancho porque es la pieza que ata las dos de
          arriba: las tres tarjetas son las tres cosas que se pueden tapar,
          y juntas se entiende que se tapan todas igual.
          -------------------------------------------------------------- */}
      <Pieza
        orden={5}
        className="gap-6 bg-indigo-50/70 sm:col-span-2 lg:col-span-4 lg:flex-row lg:items-center"
      >
        <div className="lg:max-w-md">
          <Encabezado icono={CalendarOff} tono="indigo">
            Tapás un día y nadie lo puede reservar
          </Encabezado>
          <p className="mt-3 text-sm leading-relaxed text-neutral-600">
            Elegís qué días cerrás y esas horas desaparecen de tu página.
          </p>
        </div>

        <ul className="grid flex-1 gap-2 sm:grid-cols-3">
          {BLOQUEOS_MUESTRA.map(({ icono: Icono, que }) => (
            <li key={que} className="flex items-center gap-3 rounded-2xl bg-white/80 p-3">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-indigo-100">
                <Icono className="h-4 w-4 text-indigo-600" aria-hidden="true" />
              </span>
              <p className="min-w-0 truncate text-sm font-medium text-neutral-900">{que}</p>
            </li>
          ))}
        </ul>
      </Pieza>

      {/* Recordatorios */}
      <Pieza orden={6} className="justify-between bg-amber-50/70">
        <Encabezado icono={Bell} tono="ambar">
          Recordatorios automáticos
        </Encabezado>
        <p className="mt-3 text-sm leading-relaxed text-neutral-600">
          Se mandan solos antes de cada turno
        </p>
        <p className="mt-6 text-4xl font-bold tracking-tight text-neutral-900">
          <ContadorAnimado hasta={24} sufijo="hs" />
          <span className="ml-1 align-middle text-sm font-medium text-neutral-500">
            antes
          </span>
        </p>
      </Pieza>

      {/* --------------------------------------------------------------
          Calendarios. Ocupa media fila porque tiene que entrar la lista de
          los que funcionan: decir sólo "Google Calendar" dejaba afuera a
          todo el que usa otra cosa, y son los que más dudan de si les sirve.

          La lista NO es aspiracional, sale de backend/calendarInvite.js: el
          mail de confirmación lleva un .ics adjunto, y ese formato lo abren
          los cuatro. Google además tiene conexión de ida y vuelta por OAuth
          —lo que bloqueás allá deja de ofrecerse acá—, que es distinto y por
          eso está marcado aparte.
          -------------------------------------------------------------- */}
      <Pieza orden={7} className="justify-between bg-sky-50/70 sm:col-span-2">
        <div>
          <Encabezado icono={CalendarSync} tono="azul">
            El turno se guarda solo en tu calendario
          </Encabezado>
          <p className="mt-3 text-sm leading-relaxed text-neutral-600">
            No lo tenés que anotar en ningún lado. Aparece en el calendario que ya usás.
          </p>
        </div>

        <ul className="mt-5 flex flex-wrap gap-2">
          {CALENDARIOS.map(({ nombre, doblesentido }) => (
            <li
              key={nombre}
              className="flex items-center gap-2 rounded-full bg-white px-3 py-1.5 text-sm font-medium text-neutral-700 shadow-sm"
            >
              <CalendarCheck className="h-4 w-4 text-sky-600" aria-hidden="true" />
              {nombre}
              {doblesentido && (
                <span className="rounded-full bg-sky-100 px-2 py-0.5 text-[11px] font-semibold text-sky-700">
                  ida y vuelta
                </span>
              )}
            </li>
          ))}
        </ul>
      </Pieza>

      {/* Agenda abierta */}
      <Pieza orden={8} className="items-center justify-center text-center">
        <p className="text-5xl font-bold tracking-tight text-primary sm:text-6xl">
          <ContadorAnimado hasta={24} />
          <span className="text-neutral-300">/</span>
          <ContadorAnimado hasta={7} />
        </p>
        <p className="mt-3 max-w-sm text-sm leading-relaxed text-neutral-600">
          Tu página toma turnos aunque estés durmiendo
        </p>
      </Pieza>

      {/* Sin comisiones. Va a lo ancho: con media fila quedaba un hueco a su
          derecha, porque las piezas de arriba ya habían completado esa columna. */}
      <Pieza
        orden={9}
        className="gap-6 bg-violet-50/60 sm:col-span-2 sm:flex-row sm:items-center sm:justify-between lg:col-span-4"
      >
        <div>
          <Encabezado icono={Wallet} tono="violeta">El cobro es tuyo</Encabezado>
          <p className="mt-3 max-w-lg text-sm leading-relaxed text-neutral-600">
            La plata va a tu cuenta. Nosotros no nos quedamos con nada.
          </p>
        </div>
        <p className="shrink-0 text-4xl font-bold tracking-tight text-neutral-900 sm:text-5xl">
          <ContadorAnimado hasta={0} sufijo="%" />
          <span className="ml-2 align-middle text-sm font-medium text-neutral-500">
            de comisión
          </span>
        </p>
      </Pieza>

      {/* Acá cerraba el mosaico una pieza a lo ancho con la captura del panel,
          titulada "Un panel para manejar todo". Se movió a su propia sección
          arriba del mosaico (components/business/PanelEnPerspectiva.jsx), donde
          la misma captura entra girando con el scroll y se ve en grande.

          Se sacó de acá y no se dejaron las dos porque eran idénticas: la misma
          imagen, el mismo título y la misma bajada dos veces en la misma
          página, con menos de una pantalla de distancia. */}
    </div>
  );
}
